import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useAppSelector } from "../../state/store";
import { Grid } from "@mui/material";
import { Ruling } from "../../components/Ruling";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { shuffle } from "../../util/generalUtil";
import { Row } from "../../components/Row";
import { Check } from "../../components/Check/Check";

interface Reference {
  book: number;
  chapter: number;
  verse: number;
}
type Phases = "BEFOREHAND" | "READY" | "NUMBER" | "GUESS" | "REVIEW";

export function QuestionDrill() {
  useDocumentTitle("Question Drill - Bible Quiz Academy");
  const [mode, setMode] = useState<"ALL" | "INTER">("ALL");
  const [randomized, setRandomized] = useState<Reference[]>([]);
  const [firstWord, setFirstWord] = useState("");
  const [previousVerse, setPreviousVerse] = useState<Reference>();
  const material = useAppSelector((state) => state.material.material);
  const settings = useAppSelector((state) => state.settings);
  const chapters = settings.selectedChapters;
  const [phase, setPhase] = useState<Phases>("BEFOREHAND");
  const [autoCall, setAutoCall] = useState<"" | "CORRECT" | "INCORRECT">("");
  const stats = useRef<{
    totalVerses: number;
    correct: number;
    incorrect: number;
  }>({
    totalVerses: 0,
    correct: 0,
    incorrect: 0,
  });

  useEffect(() => {
    if (material.length === 0) return;
    const allVerses: Reference[] = [];
    material.forEach((book, index) => {
      book.chapters.forEach((chapter, chapterIndex) => {
        if (chapters && chapters[index] && chapters[index][chapterIndex]) {
          chapter.forEach((_, verseIndex) => {
            const chapterFromSettings = chapters[index][chapterIndex];
            const chapterSettings =
              typeof chapterFromSettings === "boolean"
                ? Array(chapter.length).fill(chapterFromSettings)
                : chapterFromSettings;
            if (chapterSettings[verseIndex]) {
              allVerses.push({
                book: index,
                chapter: chapterIndex,
                verse: verseIndex,
              });
            }
          });
        }
      });
    });
    if (settings.vcRandomize === "RANDOM") {
      shuffle(allVerses);
    } else if (settings.vcRandomize === "REVERSE") {
      allVerses.reverse();
    }
    setRandomized(allVerses);
    stats.current.totalVerses = allVerses.length;
  }, [material]);

  const currentReference = randomized[0];
  const currentVerse = currentReference
    ? material[currentReference.book].chapters[currentReference.chapter][
        currentReference.verse
      ]
    : undefined;
  const questionCount = currentVerse
    ? mode === "INTER"
      ? currentVerse.questions.filter((q) => q.startsWith(firstWord)).length
      : currentVerse.questions.length
    : 0;

  const makeRuling = (rulingType: number) => {
    const newRandomized = [...randomized];
    newRandomized.splice(0, 1);
    if (rulingType === 1 && settings.vcReview)
      newRandomized.push(currentReference);
    setRandomized(newRandomized);
    if (rulingType === 0) stats.current.correct += 1;
    if (rulingType === 1) stats.current.incorrect += 1;
    setPreviousVerse(currentReference);
    setPhase("READY");
  };

  const clickQuestionCount = (count: number) => {
    if (currentVerse) {
      if ((count >= 3 && questionCount >= 3) || count === questionCount) {
        // Correct
        if (count >= 1) {
          // Move on
          setPhase("GUESS");
        } else {
          // Auto correct
          makeRuling(0);
          setAutoCall("CORRECT");
        }
      } else {
        // Incorrect
        makeRuling(1);
        setAutoCall("INCORRECT");
      }
    }
  };

  const referenceDisplay = (reference: Reference) => {
    return (
      <p>
        {`${material[reference.book].bookName} ${reference.chapter + 1}:${
          reference.verse + 1
        }`}
      </p>
    );
  };
  const verseDisplay = (
    reference: Reference,
    showVerse: boolean,
    showQuestions: boolean
  ) => {
    const thisVerse =
      material[reference.book].chapters[reference.chapter][reference.verse];
    const primaryQuestions: string[] = [];
    const secondaryQuestions: string[] = [];
    thisVerse.questions.forEach((question) => {
      if (mode === "ALL" || question.startsWith(firstWord)) {
        primaryQuestions.push(question);
      } else {
        secondaryQuestions.push(question);
      }
    });
    return (
      <>
        {referenceDisplay(reference)}
        {phase === "READY" && autoCall && (
          <p style={{ fontSize: 20 }}>
            {autoCall === "CORRECT" ? "Correct!" : "Incorrect"}
            {autoCall === "INCORRECT"
              ? ` - ${primaryQuestions.length} Question${
                  primaryQuestions.length === 1 ? "" : "s"
                }`
              : ""}
          </p>
        )}
        {showVerse && <p style={{ marginBottom: 20 }}>{thisVerse.verse}</p>}
        {showQuestions && (
          <>
            {primaryQuestions.length === 0 && (
              <p>{mode === "ALL" ? "Quote Only" : "No Questions"}</p>
            )}
            {primaryQuestions.map((question, index) => (
              <p key={index} style={{ margin: 0 }}>
                {question}
              </p>
            ))}
            {secondaryQuestions.map((question, index) => (
              <p
                key={index}
                style={{
                  margin: 0,
                  fontSize: 12,
                  marginTop: index === 0 ? 10 : 0,
                }}
              >
                {question}
              </p>
            ))}
          </>
        )}
      </>
    );
  };
  const getNumberGuess = (count: number, display: string) => {
    return (
      <Grid>
        <button
          className="clickable"
          onClick={() => clickQuestionCount(count)}
          aria-label={display.length > 3 ? display : `${count} Questions`}
        >
          {display}
        </button>
      </Grid>
    );
  };
  return (
    <div className="page">
      {phase === "BEFOREHAND" && (
        <div>
          <h1>Question Drill</h1>
          <p>
            This is an advanced mode to test your knowledge of the questions in
            a verse.
          </p>
          <Grid container style={{ maxWidth: 600 }} spacing={2}>
            <Grid size={6}>
              <Check checked={mode === "ALL"} onClick={() => setMode("ALL")}>
                All Question Mode
              </Check>
              <p style={{ paddingLeft: 2 }}>
                You will be given just the reference, and must figure out all
                the questions in the verse.
              </p>
            </Grid>
            <Grid size={6}>
              <Check
                checked={mode === "INTER"}
                onClick={() => setMode("INTER")}
              >
                Interrogative Mode
              </Check>
              <p style={{ paddingLeft: 2 }}>
                You will be given the reference and a word, and must figure out
                all questions beginning with that word.
              </p>
            </Grid>
          </Grid>
          <Row style={{ marginTop: 10 }}>
            <button
              className="clickable"
              onClick={() => setPhase("READY")}
              style={{ marginRight: 10 }}
            >
              Start!
            </button>
            <Link className="secondary-btn" to="/">
              Go Back
            </Link>
          </Row>
        </div>
      )}
      <div style={{ minHeight: 150, marginBottom: 20 }}>
        {phase === "READY" &&
          previousVerse &&
          verseDisplay(previousVerse, true, true)}
        {currentVerse &&
          ["NUMBER", "GUESS"].includes(phase) &&
          verseDisplay(currentReference, false, false)}
        {(phase === "NUMBER" || phase === "GUESS") && mode === "INTER" && (
          <p>{`First Word: ${firstWord}`}</p>
        )}
        {phase === "NUMBER" && (
          <>
            <p>{`How many questions ${
              mode === "INTER" ? `starting with ${firstWord} ` : ""
            }are in this verse?`}</p>
            <Grid container spacing={2}>
              {getNumberGuess(
                0,
                mode === "INTER" ? "No Questions" : "Quote Only"
              )}
              {getNumberGuess(1, "1")}
              {getNumberGuess(2, "2")}
              {getNumberGuess(3, "3+")}
            </Grid>
          </>
        )}
        {phase === "GUESS" && currentVerse && (
          <>
            <p>{`${questionCount} Question${
              questionCount === 1 ? "" : "s"
            } in the verse`}</p>
            <p>{`Try to figure out what ${questionCount === 1 ? "it is" : "they are"}!`}</p>
            <button className="clickable" onClick={() => setPhase("REVIEW")}>
              Show Questions
            </button>
          </>
        )}
        {currentVerse &&
          phase === "REVIEW" &&
          verseDisplay(currentReference, true, true)}
        {phase === "REVIEW" && currentVerse && (
          <Ruling
            hideThrowout
            onClick={(ruling) => {
              makeRuling(ruling);
              setAutoCall("");
            }}
            style={{ marginTop: 10 }}
          />
        )}
      </div>
      {phase === "READY" && randomized.length > 0 && (
        <button
          className="clickable"
          onClick={() => {
            setPhase("NUMBER");

            if (mode === "INTER" && currentVerse) {
              if (Math.random() < 0.1 || currentVerse.questions.length === 0) {
                // Random interrogative
                const possibleFirstWords: string[] = [
                  "Who",
                  "Whom",
                  "What",
                  "When",
                  "Where",
                  "Why",
                  "How",
                ];
                setFirstWord(
                  possibleFirstWords[
                    Math.floor(Math.random() * possibleFirstWords.length)
                  ] + " "
                );
              } else {
                // Random first word of questions in the verse
                const index = Math.floor(
                  Math.random() * currentVerse.questions.length
                );
                const question = currentVerse.questions[index];
                setFirstWord(
                  question.substring(0, question.indexOf(" ")) + " "
                );
              }
            }
          }}
        >
          Next Verse
        </button>
      )}
      {phase === "READY" && (
        <div style={{ marginTop: 20 }}>
          <p
            style={{ margin: 0 }}
          >{`Total Verses: ${stats.current.totalVerses}`}</p>
          <p style={{ margin: 0 }}>{`Verses Remaining: ${
            stats.current.totalVerses - stats.current.correct
          }`}</p>
          <p
            style={{ margin: 0 }}
          >{`Verses Correct: ${stats.current.correct}`}</p>
          <p
            style={{ margin: 0 }}
          >{`Verses Incorrect: ${stats.current.incorrect}`}</p>
          <div style={{ marginTop: 25 }}>
            <Link className="secondary-btn" to="/">
              Go Back
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
